import React, { useState, useEffect } from "react";
import { useStateValue } from "../../../State";

import Languages from "../../../translations";

import SwipeableViews from "react-swipeable-views";
import MobileStepper from "@mui/material/MobileStepper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

const ReadOnlyEntry = ({ value, saved }) => (
  <Box>
    <Typography
      variant="body1"
      sx={{
        whiteSpace: "pre-wrap",
        color: value ? "black" : "#464646",
        minHeight: 24,
        px: 1,
        pb: 0.5,
      }}
    >
      {value || Languages.NoEntriesYet}
    </Typography>
    {saved && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textAlign: "left", px: 1 }}
      >
        {saved}
      </Typography>
    )}
  </Box>
);

const SupportDetailView = ({ tier }) => {
  const { state } = useStateValue();
  const SupportTiers = state.combinedPlan.data
    ? state.combinedPlan.data.SupportTiers
    : null;

  const data =
    SupportTiers == null
      ? null
      : tier === "enhanced"
        ? SupportTiers.EnhancedSupport
        : SupportTiers.SpecialSupport;

  // Build the slides from the support data
  const buildSlides = (d) => {
    if (!d) return [];
    const slides = [];

    if (d.OverallSituationText || d.OverallSituationTitle) {
      slides.push({
        Subtitle: d.OverallSituationTitle || "Kokonaistilanne",
        MainText: d.OverallSituationText || null,
        MainTextSaved: d.OverrallSituationSaved || null,
        Evaluations: [],
      });
    }

    (d.SupportItems || []).forEach((item) => {
      slides.push({
        Subtitle: item.Label,
        MainText: item.MainText || null,
        MainTextSaved: item.MainTextSaved || null,
        Evaluations: item.Evaluations || [],
      });
    });

    (d.OtherEvaluationItems || []).forEach((item) => {
      slides.push({
        Subtitle: item.Label,
        MainText: item.MainText || null,
        MainTextSaved: item.MainTextSaved || null,
        Evaluations: item.Evaluations || [],
      });
    });

    return slides;
  };

  const slides = buildSlides(data);
  const MaxSlideCount = slides.length;

  const [SlideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    setSlideIndex(0);
  }, [tier]);

  if (!SupportTiers || !data || MaxSlideCount === 0) {
    return null;
  }

  const Title =
    data.Title ||
    (tier === "enhanced"
      ? Languages.VasuEnhancedSupport
      : Languages.VasuSpecialSupport);

  const handleChangeIndex = (index) => {
    setSlideIndex(index);
  };

  const handlePrev = () => {
    if (SlideIndex > 0) setSlideIndex(SlideIndex - 1);
  };

  const handleNext = () => {
    if (SlideIndex < MaxSlideCount - 1) setSlideIndex(SlideIndex + 1);
  };

  return (
    <div className="CombinedPlanSupportDetail">
      <Box>
        <SwipeableViews
          className={"col-12 textCenter slide pt-3"}
          index={SlideIndex}
          key={SlideIndex}
          onChangeIndex={handleChangeIndex}
        >
          {slides.map((slide, index) => (
            <div key={index}>
              <Box
                className="br-top col-12"
                sx={{ backgroundColor: "#fceebf" }}
              >
                <Typography
                  color="textSecondary"
                  sx={{
                    textAlign: "center",
                    fontWeight: "Bold",
                    fontSize: 18,
                    color: "#424242",
                    pt: 1,
                  }}
                >
                  {Title}
                </Typography>
                <MobileStepper
                  style={{
                    backgroundColor: "#fceebf",
                    color: "black",
                    padding: 0,
                  }}
                  variant="text"
                  steps={MaxSlideCount}
                  position="static"
                  activeStep={SlideIndex}
                  nextButton={
                    <Button
                      size="small"
                      style={{
                        color: "black",
                        opacity: SlideIndex === MaxSlideCount - 1 ? "0.5" : "",
                      }}
                      onClick={handleNext}
                      disabled={SlideIndex === MaxSlideCount - 1}
                    >
                      {Languages.next} <KeyboardArrowRight />
                    </Button>
                  }
                  backButton={
                    <Button
                      size="small"
                      style={{
                        color: "black",
                        opacity: SlideIndex === 0 ? "0.5" : "",
                      }}
                      onClick={handlePrev}
                      disabled={SlideIndex === 0}
                    >
                      <KeyboardArrowLeft /> {Languages.prev}
                    </Button>
                  }
                />
              </Box>
              <Box
                className="br-btm col-12"
                sx={{ boxShadow: 2, mb: 3, pb: 1, backgroundColor: "white" }}
              >
                <Typography
                  color="textSecondary"
                  sx={{
                    textAlign: "center",
                    fontWeight: "Medium",
                    fontSize: 16,
                    color: "#424242",
                    pt: 1,
                  }}
                >
                  {slide.Subtitle}
                </Typography>
              </Box>
            </div>
          ))}
        </SwipeableViews>

        <Box sx={{ px: 1 }}>
          <Divider textAlign="left">
            <Chip
              label={
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: 14,
                    color: "#424242",
                  }}
                >
                  {Languages.StaffNotes}
                </Typography>
              }
              variant="outlined"
            />
          </Divider>
          <Box
            sx={{
              backgroundColor: "#fceebf",
              borderRadius: 2,
              boxShadow: 1,
              mt: 2,
              pt: 2,
              pb: 2,
              px: 1,
            }}
          >
            <ReadOnlyEntry
              value={slides[SlideIndex].MainText}
              saved={slides[SlideIndex].MainTextSaved}
            />
          </Box>

          {slides[SlideIndex].Evaluations.length > 0 && (
            <>
              <Divider textAlign="left" sx={{ mt: 2 }}>
                <Chip
                  label={
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: 14,
                        color: "#424242",
                      }}
                    >
                      {Languages.Evaluation}
                    </Typography>
                  }
                  variant="outlined"
                />
              </Divider>
              {slides[SlideIndex].Evaluations.map((evaluation, eindex) => (
                <Box
                  key={eindex}
                  sx={{
                    backgroundColor: "#fceebf",
                    borderRadius: 2,
                    boxShadow: 1,
                    mt: 2,
                    pt: 2,
                    pb: 2,
                    px: 1,
                  }}
                >
                  <ReadOnlyEntry
                    value={evaluation.Text}
                    saved={evaluation.Saved}
                  />
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default SupportDetailView;
